import { Modal, Form, Input, Button } from "antd";
import { useRef, useState, useEffect } from "react";
import Draggable from "react-draggable";

import { formItemLayout, inputLayout } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import Loading from "../../../common/Loading";
import { General } from "../../../../util/General";
import FormInputRules from "../../../../util/FormInputRules";
import styles from "../../index.module.less";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";
import { ConfigMasterConstants } from "../../../../constants/DatabaseConstants/ConfigMasterConstants";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";

type ConfigMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const ConfigMaster = (props: ConfigMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);

  /* CUSTOM HOOKS */
  const { bounds, onStart } = useReactDraggable();

  /* HANDLERS */

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !props.addRecord;
  const isOnEditLoading = !!props.addRecord;

  const validateValue = (_: any, value: any) => {
    return form.getFieldValue("key") === ConfigMasterConstants.MINS_BEFORE_SENDING_NOTIF && value < 15 ? Promise.reject(new Error("Minimum value should be 15.")) : Promise.resolve();
  };

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      form.setFieldsValue(props.rowData);
      setFormFieldsLoadedToForm(true);
    }
  }, [props.addRecord, isOnEditLoading, formFieldsLoadedToForm, props.rowData, form, setFormFieldsLoadedToForm]);

  return (
    <Modal
      afterClose={() => form.resetFields()}
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} Config Master`}</div>}
      open={true}
      onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}
      width={"50%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {((props.addRecord && isOnAddLoading) || (!props.addRecord && !formFieldsLoadedToForm)) && (
        <div className={`${styles["textCenter"]}`}>
          <Loading />
        </div>
      )}

      {((props.addRecord && !isOnAddLoading) || (!props.addRecord && formFieldsLoadedToForm)) && (
        <Form
          ref={formRef}
          onValuesChange={(changedValues) => {
            const fieldName = Object.keys(changedValues)[0];
            form.validateFields([fieldName]);
          }}
          className={`${styles["formDiv"]}`}
          {...formItemLayout}
          form={form}
          validateTrigger="onBlur"
          colon={false}
          name="register"
          onFinish={props.handleSubmit}
        >
          <Form.Item {...inputLayout} name="key" label="Key" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("key")]}>
            <Input name="key" disabled={true} />
          </Form.Item>
          <Form.Item {...inputLayout} name="value" label="Value" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("value"), { validator: validateValue }]}>
            <Input name="value" />
          </Form.Item>

          <Form.Item {...inputLayout} name="description" label="Description" normalize={General.inputWhiteSpaceRemove}>
            <Input.TextArea name="description" rows={4} />
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button htmlType="submit" className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`}>
              {props.saving ? <CircularProgressBar /> : props.addRecord ? "Submit" : "Update"}
            </Button>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button disabled={props.saving} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
              Close
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default ConfigMaster;
